import {
  calendarClickToZonedDate,
  combineDateAndTime,
  extractDurationFromNowMinus,
  formatWithTimeZone,
  getDurationStringMilliseconds,
  getNowInTimeZone,
  isDurationString,
  isLocalTimeZone,
  isNowValue,
  TimeFormat,
  zonedDateToLocalStartOfDay,
} from '@chronosphereio/core';
import { DateCalendarProps, pickersCalendarHeaderClasses } from '@mui/x-date-pickers-pro';
import { endOfDay, isSameDay, isValid, setMilliseconds, startOfDay } from 'date-fns';
import { styled } from '../../../utils';
import { Box } from '../../Box';

// Re-export for backwards compatibility
export { combineDateAndTime };

export interface BaseCalendarProps extends Pick<DateCalendarProps<Date>, 'minDate' | 'maxDate'> {
  timeFormat: TimeFormat;
  timeZone?: string;
  variant?: 'single' | 'range';
}

// Checks if two dates are on the same calendar day, in the given timezone.
const isSameCalendarDay = ({
  endDate,
  startDate,
  timeZone,
}: {
  endDate: Date | null;
  startDate: Date | null;
  timeZone?: string;
}) => {
  if (!startDate || !endDate) return false;
  if (timeZone && !isLocalTimeZone(timeZone)) {
    return (
      formatWithTimeZone(startDate, 'yyyy-MM-dd', timeZone) === formatWithTimeZone(endDate, 'yyyy-MM-dd', timeZone)
    );
  }
  // isSameDay uses browser local and would be wrong when a different timezone is set
  return isSameDay(startDate, endDate);
};

/**
 * Computes the calendar value from start/end dates, with optional support for flexible time inputs.
 * When string inputs are provided, handles relative expressions like "now-5m" to "now".
 */
export function computeCalendarValue(
  startDate: Date | null,
  endDate: Date | null,
  startInputValue?: string,
  endInputValue?: string,
  timeZone?: string
): { end: Date | null; start: Date | null } {
  let computedStart = startDate;
  let computedEnd = endDate;
  let startIsRelativeDerived = false;
  let endIsRelativeDerived = false;

  // Handle flexible inputs if provided
  if (startInputValue !== undefined || endInputValue !== undefined) {
    const now = setMilliseconds(getNowInTimeZone(timeZone), 0);

    // Handle relative end ("now")
    if (!computedEnd && endInputValue && isNowValue(endInputValue)) {
      computedEnd = now;
      endIsRelativeDerived = true;
    }

    // Handle relative start ("now-x" or bare duration like "5m")
    if (!computedStart && startInputValue) {
      const durationFromNowMinus = extractDurationFromNowMinus(startInputValue);
      const duration = durationFromNowMinus ?? (isDurationString(startInputValue) ? startInputValue : undefined);

      if (duration && isDurationString(duration)) {
        const durationMs = getDurationStringMilliseconds(duration);
        if (durationMs) {
          computedStart = new Date(now.getTime() - durationMs);
          startIsRelativeDerived = true;
        }
      }
    }
  }

  // The date range calendar crashes when the dates are invalid, which can happen when a user is typing out dates manually.
  // The calendar also works better with the times being set to the start of day, so we modify that for the input to avoid
  // issues when we retain the time from the text inputs (primarily happens with selecting a single day).
  // When dates are relative-derived, they are already in the target timezone. Otherwise we convert them to the target timezone.
  const toCalendarDate = (d: Date, isRelativeDerived: boolean) => {
    if (timeZone && !isLocalTimeZone(timeZone)) {
      return isRelativeDerived ? startOfDay(d) : zonedDateToLocalStartOfDay(d, timeZone);
    }
    return startOfDay(d);
  };
  const startTime =
    computedStart && isValid(computedStart) ? toCalendarDate(computedStart, startIsRelativeDerived) : null;
  const endTime = computedEnd && isValid(computedEnd) ? toCalendarDate(computedEnd, endIsRelativeDerived) : null;
  return { start: startTime, end: endTime };
}

/**
 * Processes a date range selection from the calendar, applying time preservation
 * and handling edge cases like same-day selection.
 */
export function processDateRangeSelection(
  value: [Date | null, Date | null],
  options: {
    existingEndTime: Date | null;
    existingStartTime: Date | null;
    isSelectingNewStart: boolean;
    isTimeModified: boolean;
    /** When set with isTimeModified, time is taken from existing times in this zone (avoids local-getter mismatch). */
    timeZone?: string;
  }
): { end: Date | null; start: Date | null } {
  // Does not need to be adjusted to beginning of the day because it defaults to it already.
  let startDate = value[0];
  // End of the selected day: use display timezone when set so the end date doesn't shift to the next
  // calendar day when the browser is in a different zone.
  let endDate: Date | null = null;
  if (value[1]) {
    if (options.timeZone && !isLocalTimeZone(options.timeZone)) {
      endDate = calendarClickToZonedDate(value[1], options.timeZone, true);
    } else {
      endDate = endOfDay(value[1]);
    }
    endDate.setMilliseconds(0);
  }

  // If we just started selecting a new start date, ignore any stale end date from MUI
  if (options.isSelectingNewStart) {
    endDate = null;
  }

  if (options.isTimeModified && startDate && options.existingStartTime) {
    // If time was modified already, retain that time (instead of using start-of-day).
    startDate = combineDateAndTime(startDate, options.existingStartTime, options.timeZone);
  }

  // Use value[1] (raw calendar date) as the date source so combineDateAndTime's local getters
  // reflect the clicked day; passing the already-converted endDate would use browser-local date
  // and can shift by one day when browser and target timezone differ.
  if (options.isTimeModified && endDate && options.existingEndTime && value[1]) {
    endDate = combineDateAndTime(value[1], options.existingEndTime, options.timeZone);
  }

  // After the end date is selected, if it turns out the user has selected a single date
  // we reset the times back to start-to-end of the day instead of using the modified time.
  // This (1) avoids accidentally selecting an invalid time where the end is before the start,
  // which would be much easier with a single day (2) gives users a bailout option to reset time
  // to start-to-end of day. When a display timezone is set, compare calendar day in that zone
  const sameCalendarDay = isSameCalendarDay({ startDate, endDate, timeZone: options.timeZone });
  if (options.isTimeModified && startDate && endDate && sameCalendarDay && value[0] && value[1]) {
    if (options.timeZone && !isLocalTimeZone(options.timeZone)) {
      startDate = calendarClickToZonedDate(value[0], options.timeZone, false);
      endDate = calendarClickToZonedDate(value[1], options.timeZone, true);
      endDate.setMilliseconds(0);
    } else {
      startDate = startOfDay(startDate);
      endDate = endOfDay(endDate);
      endDate.setMilliseconds(0);
    }
  }

  return { start: startDate, end: endDate };
}

export const BaseInputCalendarWrapper = styled(Box)({
  alignSelf: 'center',
  [`.${pickersCalendarHeaderClasses.labelContainer}`]: {
    cursor: 'pointer',
    zIndex: 1,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});
